<template>
  <OurModal
    v-model="isShownModal"
    :title="$t('promotions.products.addByCategory')"
    width="3xl"
    color="gray"
  >
    <div class="categories-tree-modal">
      <div
        class="toolbar mb-3 flex flex-wrap items-center gap-3 border-b border-solid border-gray-100 pb-3"
      >
        <OurButton
          variant="thirdary"
          filled
          size="sm"
          :text="$t('categories.tree.expandAll')"
          @click="expandAllBranches"
        />

        <OurButton
          variant="thirdary"
          filled
          size="sm"
          :text="$t('categories.tree.collapseAll')"
          @click="collapseAllBranches"
        />
      </div>

      <div class="rounded-xl border border-solid border-gray-200 bg-white px-4 py-2">
        <OurTableList
          ref="categoriesTreeTable"
          nesting
          collapsible-nesting
          :list="categoriesTree"
          :have-permission-delete="false"
          :have-permission-edit="false"
          :is-draggable="false"
        >
          <template #default="{ item }">
            <div
              class="tree-row-label cursor-pointer text-base leading-snug"
              :class="{ 'font-medium text-blue-600': selectedCategoryId === item.id }"
              role="button"
              tabindex="0"
              @click="onSelectCategory(item.id)"
            >
              {{ item.name }}
            </div>
          </template>
        </OurTableList>
      </div>
    </div>

    <template #footer-left>
      <OurButton
        :text="$t('promotions.products.add')"
        :disabled="!selectedCategoryId"
        @click="onClickApplyButton"
      />
      <OurButton :text="$t('button.close')" variant="secondary" @click="onClickCloseButton" />
    </template>
  </OurModal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

function compareCategorySort(a, b) {
  return Number(a.sort) - Number(b.sort);
}

export default {
  name: "AddProductsByCategoryModal",

  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedCategoryId: null,
      categoriesTree: [],
    };
  },

  computed: {
    ...mapGetters("catalogCategories", ["getCatalogCategoryRecords"]),

    isShownModal: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
  },

  watch: {
    show(value) {
      if (value) {
        this.loadCategories();
      } else {
        this.selectedCategoryId = null;
      }
    },

    getCatalogCategoryRecords: {
      handler() {
        this.buildCategoriesTree();
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions("catalogCategories", ["fetchNewCatalogCategories"]),

    async loadCategories() {
      await this.fetchNewCatalogCategories({ withLoader: false });
      this.buildCategoriesTree();
    },

    buildCategoriesTree() {
      const items = this.getCatalogCategoryRecords.map((category) => ({
        id: category.id,
        name: category.name,
        sort: category.sort,
        parent: {
          id: category.parentId || null,
          name: category.parent?.name || "",
        },
      }));

      const buildNode = (item) => {
        const node = { ...item, children: [] };
        const children = items
          .filter((row) => row.parent?.id === item.id)
          .sort(compareCategorySort)
          .map((child) => buildNode(child));

        node.children = children;

        return node;
      };

      this.categoriesTree = items
        .filter((item) => !item.parent?.id)
        .sort(compareCategorySort)
        .map((root) => buildNode(root));
    },

    onSelectCategory(id) {
      this.selectedCategoryId = id;
    },

    expandAllBranches() {
      this.$refs.categoriesTreeTable?.expandAllBranches?.();
    },

    collapseAllBranches() {
      this.$refs.categoriesTreeTable?.collapseAllBranches?.();
    },

    closeModal() {
      this.isShownModal = false;
    },

    onClickCloseButton() {
      this.closeModal();
    },

    onClickApplyButton() {
      if (!this.selectedCategoryId) {
        return;
      }

      const selectedCategory = this.getCatalogCategoryRecords.find(
        (category) => category.id === this.selectedCategoryId
      );

      if (!selectedCategory) {
        this.closeModal();

        return;
      }

      this.$emit("add-category", {
        id: selectedCategory.id,
        name: selectedCategory.name,
      });
    },
  },
};
</script>
